import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  OpenInNew,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { chain, toString } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Progress } from 'src/components/progress';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import { fetchAssemblyComponents } from 'src/hooks';
import { lang, useUserRole } from 'src/utils';

import { findMatchedAssemblyComponents } from '../../DetailAssemblyPage.helper';
import {
  AlertContainer,
  AssemblyDetails,
  SummaryLabel,
} from '../AssemblyDetailPageSidebar/AssemblyDetailPageSidebar.styled';
import {
  AccType,
  Component,
  DetailAssemblyComponentsProps,
  IBackupList,
  IComponent,
  IComponentListByJourney,
} from './DetailAssemblyComponentList.interface';
import {
  DateWrapper,
  StyledAccordion,
  Summary,
} from './DetailAssemblyComponentList.styled';

const ComponentName = ({ name }: { name: string }) => {
  return (
    <CustomTooltip
      width={500}
      placement="right"
      title={name}
      overflowLineNumber={1}>
      <p>{name}</p>
    </CustomTooltip>
  );
};

export const DetailAssemblyComponentList = (
  props: DetailAssemblyComponentsProps,
) => {
  const { layouts, assemblyComponents, parentAssemblyComponents, assemblyId } =
    props;

  const [componentList, setComponentList] = useState<IComponentListByJourney>(
    {},
  );

  const { isOwnerOperator } = useUserRole();

  const [loading, setLoading] = useState(false);
  const hasRun = useRef(false);

  const mergeComponents = useCallback(
    (original: IComponentListByJourney, backup: IBackupList[]) => {
      // Create a map from backup types to their components based on screen_section_id

      Object.keys(original).forEach((typeKey) => {
        original[typeKey] = mergeComponentArrays(original[typeKey], backup);
      });

      return original;
    },
    [],
  );

  function mergeComponentArrays(
    originalComponents: Component[],
    backupComponents: IBackupList[],
  ): Component[] {
    const mergedArray = [...originalComponents];

    backupComponents.forEach((replaceGroup) => {
      replaceGroup.components.forEach((backupComp) => {
        const targetGroup = mergedArray.find(
          (group) =>
            group.type === replaceGroup.type &&
            group.components.some(
              (component) =>
                component.layout_screen_id === backupComp.layout_screen_id &&
                component.screen_section_id === backupComp.screen_section_id,
            ),
        );

        if (
          targetGroup &&
          !targetGroup.components.some((c) => c.id === backupComp.id)
        ) {
          targetGroup.components = targetGroup.components.concat(backupComp);
        }
      });
    });

    return mergedArray;
  }

  const prepareComponents = async () => {
    setLoading(true);
    const { backupAssemblyComponents } = await fetchAssemblyComponents(
      toString(assemblyId),
      undefined,
      ['componentTypes'],
    );

    const componentDetails =
      backupAssemblyComponents?.map((item) => {
        return {
          type: item?.component?.component_type?.name,
          components: [],
          component: {
            name: item.component?.name,
            id: item.component?.id,
            backup_order: item.backup_order,
            layout_screen_id: item.layout_screen_id,
            screen_section_id: item.screen_section_id,
            rotation_order: item.rotation_order,
            start_date: item?.component?.start_date,
            end_date: item?.component?.end_date,
          },
        };
      }) ?? [];

    const groupedByType = componentDetails.reduce((acc: AccType, item) => {
      if (item.type) {
        if (!acc[item.type]) {
          acc[item.type] = {
            type: item.type,
            components: [],
          };
        }

        acc[item.type].components = acc[item.type].components.concat(
          item.component as IComponent,
        );
      }

      return acc;
    }, {} as AccType);

    // Convert the groupedByType object into an array of the desired structure
    const groupedArray: IBackupList[] = Object.values(groupedByType);

    const mergedArray = mergeComponents(componentList, groupedArray);
    setComponentList(mergedArray);
    setLoading(false);
  };

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    prepareComponents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assemblyId]);

  const groupByType = (data: Component[]): { [key: string]: Component[] } => {
    return data.reduce((acc, item) => {
      if (!acc[item.type]) {
        acc[item.type] = [];
      }
      acc[item.type].push(item);

      return acc;
    }, {} as { [key: string]: Component[] });
  };

  useMemo(() => {
    let details: Component[] = [];
    layouts.forEach((layout) => {
      const { screens } = layout;

      screens.forEach((screen) => {
        screen.sections.forEach((section) => {
          const components = findMatchedAssemblyComponents(
            assemblyComponents,
            screen.layoutScreenId,
            section.screenSectionId,
            ['rotation_order'],
            parentAssemblyComponents,
          );

          const componentDetails =
            components?.map((item) => {
              return {
                type: item?.component?.component_type?.name,
                components: [],
                component: {
                  name: item.component?.name,
                  id: item.component?.id,
                  backup_order: item.backup_order,
                  layout_screen_id: item.layout_screen_id,
                  screen_section_id: item.screen_section_id,
                  rotation_order: item.rotation_order,
                  start_date: item?.component?.start_date,
                  end_date: item?.component?.end_date,
                },
              };
            }) ?? [];

          const groupedByType = componentDetails.reduce(
            (acc: AccType, item) => {
              if (item.type) {
                if (!acc[item.type]) {
                  acc[item.type] = {
                    type: item.type,
                    components: [],
                  };
                }

                acc[item.type].components = acc[item.type].components.concat(
                  item.component as IComponent,
                );
              }

              return acc;
            },
            {} as AccType,
          );

          // Convert the groupedByType object into an array of the desired structure
          const groupedArray = Object.values(groupedByType);
          details.push(...groupedArray);
        });
      });
    });

    const types = groupByType(details);
    setComponentList(types);
  }, [layouts, assemblyComponents, parentAssemblyComponents]);

  return (
    <Box mt={1}>
      {loading ? (
        <Progress />
      ) : (
        <>
          {componentList && Object.keys(componentList).length > 0 ? (
            <>
              {Object.keys(componentList).map((type, idx) => (
                <StyledAccordion key={idx}>
                  <Summary
                    expandIcon={<KeyboardArrowDownIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header">
                    <SummaryLabel>
                      {chain(type).replace(/-/g, ' ').startCase().value()}
                    </SummaryLabel>
                  </Summary>
                  <AssemblyDetails>
                    {componentList[type].map(
                      (component: Component, index: number) => (
                        <Box key={index}>
                          {component.components
                            .sort((a, b) => a.rotation_order - b.rotation_order)
                            .map((compItem, idx: number) => (
                              <Box key={idx} sx={{ paddingTop: '4px' }}>
                                {compItem.rotation_order === 0 &&
                                  compItem.backup_order === 0 && (
                                    <Typography variant="subtitle2">
                                      {lang.defaultComponent}
                                    </Typography>
                                  )}
                                {compItem.rotation_order > 0 && (
                                  <Typography variant="subtitle2">
                                    {lang.additionalComponent}
                                  </Typography>
                                )}
                                {compItem.backup_order > 0 && (
                                  <Typography variant="subtitle2">
                                    {lang.backupComponent}
                                  </Typography>
                                )}
                                {(compItem?.start_date ||
                                  compItem?.end_date) && (
                                  <DateWrapper>
                                    <Alert severity="info">
                                      {lang.softSellInfo.dateDescription(
                                        compItem?.start_date,
                                        compItem?.end_date,
                                      )}
                                    </Alert>
                                  </DateWrapper>
                                )}
                                <Stack
                                  key={compItem.id}
                                  direction="row"
                                  alignItems="center"
                                  spacing={2}
                                  sx={{ width: '100%' }}>
                                  <Box sx={{ width: '15%' }}>{compItem.id}</Box>
                                  <Box sx={{ width: '68%' }}>
                                    <ComponentName name={compItem.name} />
                                  </Box>
                                  {!isOwnerOperator && (
                                    <Box sx={{ width: '15%' }}>
                                      <IconButton
                                        title={lang.openInNewTab}
                                        color="primary"
                                        aria-label={lang.openInNewTab}
                                        to={`${PATHS.COMPONENT_DETAILS(
                                          compItem.id,
                                        )}`}
                                        component={Link}
                                        target="_blank">
                                        <OpenInNew color="primary" />
                                      </IconButton>
                                    </Box>
                                  )}
                                </Stack>
                              </Box>
                            ))}
                          <Divider />
                        </Box>
                      ),
                    )}
                  </AssemblyDetails>
                </StyledAccordion>
              ))}
            </>
          ) : (
            <AlertContainer>
              <Alert severity="error">{lang.noData}</Alert>
            </AlertContainer>
          )}
        </>
      )}
    </Box>
  );
};
